<template>
  <div class="episode-meta-editor">
    <!-- Locked: no full episode uploaded yet -->
    <div v-if="isLocked" class="meta-locked">
      <span class="icon is-large has-text-grey-light">
        <font-awesome-icon :icon="['fal', 'lock']" />
      </span>
      <p class="has-text-grey">Upload the <strong>Full Episode</strong> to author the title &amp; show notes.</p>
    </div>

    <template v-else>
      <!-- Transcription + generation controls -->
      <div class="meta-toolbar">
        <!-- Done: view the transcript in a drawer (re-transcribe lives inside it) -->
        <template v-if="hasTranscript">
          <button class="button is-small" @click="openTranscript">
            <span class="icon"><font-awesome-icon :icon="['fal', 'file-lines']" /></span>
            <span>View Transcript</span>
          </button>
        </template>
        <!-- Not yet transcribed -->
        <button
          v-else
          class="button is-small"
          :class="{ 'is-loading': isTranscribing }"
          :disabled="!canTranscribe"
          @click="onTranscribe"
        >
          <span class="icon"><font-awesome-icon :icon="['fal', 'waveform-lines']" /></span>
          <span>{{ transcribeLabel }}</span>
        </button>

        <button
          class="button is-small is-primary"
          :class="{ 'is-loading': generating }"
          :disabled="!canGenerate"
          @click="onGenerate"
        >
          <span class="icon"><font-awesome-icon :icon="['fal', 'wand-magic-sparkles']" /></span>
          <span>Generate title &amp; show notes</span>
        </button>

        <span v-if="!encodeReady" class="is-size-7 has-text-grey ml-2">Full episode still encoding…</span>
        <span v-else-if="transcriptStatus === 'failed'" class="is-size-7 has-text-danger ml-2">Transcription failed — retry.</span>
        <span v-else-if="!hasTranscript && transcriptStatus !== 'processing'" class="is-size-7 has-text-grey ml-2">Transcribe first to enable generation.</span>
      </div>

      <!-- Full episode revised since this content was written: admin decides -->
      <div v-if="isStale" class="meta-stale">
        <span class="icon has-text-warning-dark"><font-awesome-icon :icon="['fal', 'clock-rotate-left']" /></span>
        <span class="meta-stale-text">
          The full episode was revised after this was written.
          <span v-if="isTranscribing" class="has-text-grey">Re-transcribing the new cut…</span>
        </span>
        <button class="button is-small" @click="keepContent">Keep as is</button>
        <button
          class="button is-small is-primary"
          :class="{ 'is-loading': generating }"
          :disabled="!canGenerate || isTranscribing"
          @click="onGenerate"
        >Regenerate</button>
      </div>
      <p v-else-if="previousContent" class="is-size-7 has-text-grey">
        Regenerated.
        <a @click="restorePrevious">Restore previous version</a>
      </p>

      <!-- Title -->
      <div class="field">
        <label class="label is-small">Episode Title</label>
        <input
          v-model="title"
          class="input"
          placeholder="Public-facing episode title"
          @blur="saveField('episodeTitle', title)"
        />
      </div>

      <!-- On-thumbnail text -->
      <div class="field">
        <label class="label is-small">On-Thumbnail Text</label>
        <input
          v-model="thumbnailText"
          class="input is-small"
          placeholder="2–6 bold words for the thumbnail"
          @blur="saveField('episodeThumbnailText', thumbnailText)"
        />
        <p class="help">Short text overlaid on the thumbnail — must not repeat the title.</p>
      </div>

      <!-- Show notes (markdown WYSIWYG) -->
      <div class="field meta-notes-field">
        <label class="label is-small">Show Notes</label>
        <MarkdownEditor v-model="showNotes" placeholder="Episode description, key takeaways, timestamps…" />
        <div class="meta-notes-actions">
          <button
            class="button is-small"
            :class="notesDirty ? 'is-primary' : ''"
            :disabled="!notesDirty"
            @click="saveField('episodeDescription', showNotes)"
          >
            <span class="icon is-small"><font-awesome-icon :icon="['fal', notesDirty ? 'floppy-disk' : 'check']" /></span>
            <span>{{ notesDirty ? 'Save show notes' : 'Saved' }}</span>
          </button>
        </div>
      </div>
    </template>

    <TranscriptDrawer
      v-model="showTranscript"
      :vtt="transcriptData.vtt || transcriptData.text"
      :words="transcriptData.words"
      :word-level="transcriptData.wordLevel"
      :source="transcriptData.source"
      :editable="false"
      filename-base="episode-transcript"
    >
      <template #footer-start>
        <button class="button is-small" :class="{ 'is-loading': isTranscribing }" :disabled="isTranscribing" @click="onTranscribe">
          <span class="icon is-small"><font-awesome-icon :icon="['fal', 'waveform-lines']" /></span>
          <span>Re-transcribe</span>
        </button>
      </template>
    </TranscriptDrawer>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'
import MarkdownEditor from '@/components/common/MarkdownEditor.vue'
import TranscriptDrawer from '@/components/assets/TranscriptDrawer.vue'
import { episodeService } from '@/services/episodeService'
import { openAIService } from '@/services/openAIService'
import { episodeContentPromptService } from '@/services/episodeContentPromptService'
import { enqueueEpisodeTranscription, getEpisodeTranscriptData } from '@/services/transcriptionService'
import { logger } from '@/services/loggerService'
import { toastBridge as toast } from '@/services/toastBridge'

const props = defineProps({
  project: { type: Object, required: true }, // live episode project doc
  reviewId: { type: String, required: true }, // === deliverable id
  fullEpisodeItem: { type: Object, default: null }, // the full_episode slot's review item
})

const title = ref(props.project?.episodeTitle || '')
const thumbnailText = ref(props.project?.episodeThumbnailText || '')
const showNotes = ref(props.project?.episodeDescription || '')
const generating = ref(false)
const showTranscript = ref(false)
const transcriptData = ref({ text: '', vtt: '', words: [], wordLevel: false, source: '' })

// Keep local fields in sync when the project doc changes underneath us, but
// don't clobber a field the user is actively editing.
watch(() => props.project?.episodeTitle, (v) => { if (document.activeElement?.tagName !== 'INPUT') title.value = v || '' })
watch(() => props.project?.episodeThumbnailText, (v) => { if (document.activeElement?.tagName !== 'INPUT') thumbnailText.value = v || '' })

const hasContent = computed(() => !!(props.project?.episodeTitle || props.project?.episodeDescription || props.project?.episodeThumbnailText))
const isStale = computed(() => !!props.project?.episodeContentStale && hasContent.value)
const previousContent = computed(() => props.project?.episodeContentPrevious || null)

const notesDirty = computed(() => (showNotes.value || '') !== (props.project?.episodeDescription || ''))

// ── Lock / readiness ─────────────────────────────────────────
const isLocked = computed(() => !props.fullEpisodeItem)
const currentFullVersion = computed(() => {
  const item = props.fullEpisodeItem
  if (!item) return null
  const versions = item.versions || []
  return versions.find((v) => v.id === item.currentVersionId) || versions[versions.length - 1] || null
})
// Bunny populates mp4Variants only AFTER encoding — the real "ready to transcribe"
// signal (status===4 lies, per project_bunny_transcode_status_flag).
const encodeReady = computed(() => {
  const v = currentFullVersion.value
  return !!(v?.bunnyStream?.mp4Variants && v.bunnyStream.mp4Variants.length)
})

// `project` is live-subscribed by the parent (DeliverableReviewTab), so the
// transcription status flips (Transcribing… → completed) reactively — no local
// listener needed.
const transcriptStatus = computed(() => props.project?.episodeTranscriptStatus || null)
// Optimistic: click → enqueue → backend flips status to 'processing' takes a while.
// Bridge that gap so the button shows loading immediately. Cleared once the server
// status catches up (processing/completed/failed).
const enqueuing = ref(false)
const isTranscribing = computed(() => enqueuing.value || transcriptStatus.value === 'processing')
const hasTranscript = computed(() => transcriptStatus.value === 'completed')

const canTranscribe = computed(() => encodeReady.value && !isTranscribing.value)
const canGenerate = computed(() => hasTranscript.value && !generating.value)

const transcribeLabel = computed(() => {
  if (isTranscribing.value) return 'Transcribing…'
  if (transcriptStatus.value === 'completed') return 'Re-transcribe'
  if (transcriptStatus.value === 'failed') return 'Retry transcription'
  return 'Generate transcription'
})

// ── Actions ──────────────────────────────────────────────────
async function saveField(field, value) {
  if ((props.project?.[field] || '') === (value || '')) return
  try {
    await episodeService.updateEpisode(props.project.id, { [field]: value })
  } catch (err) {
    logger.error('[EpisodeMetaEditor] Save failed', { field, error: err.message })
    toast.error('Failed to save')
  }
}

async function openTranscript() {
  try {
    transcriptData.value = await getEpisodeTranscriptData(props.project.id)
    showTranscript.value = true
  } catch (err) {
    logger.error('[EpisodeMetaEditor] Failed to load transcript', { error: err.message })
    toast.error('Failed to load transcript')
  }
}

async function onTranscribe() {
  enqueuing.value = true
  try {
    await enqueueEpisodeTranscription(props.project.id, props.reviewId)
    toast.info('Transcription started — this can take a few minutes.')
  } catch (err) {
    logger.error('[EpisodeMetaEditor] Transcribe enqueue failed', { error: err.message })
    toast.error('Failed to start transcription')
    enqueuing.value = false
  }
}

// Hand off the optimistic flag to the server status once it lands.
watch(transcriptStatus, (s) => { if (s) enqueuing.value = false })

async function keepContent() {
  try {
    await episodeService.updateEpisode(
      props.project.id,
      { episodeContentStale: false },
      { action: 'episode_content_kept' }
    )
  } catch (err) {
    toast.error(err.message || 'Failed to update')
  }
}

async function restorePrevious() {
  const prev = previousContent.value
  if (!prev) return
  try {
    await episodeService.updateEpisode(
      props.project.id,
      { ...prev, episodeContentPrevious: null },
      { note: 'Restored previous version' }
    )
    title.value = prev.episodeTitle || ''
    thumbnailText.value = prev.episodeThumbnailText || ''
    showNotes.value = prev.episodeDescription || ''
    toast.success('Previous title & show notes restored.')
  } catch (err) {
    logger.error('[EpisodeMetaEditor] Restore failed', { error: err.message })
    toast.error(err.message || 'Failed to restore')
  }
}

async function onGenerate() {
  generating.value = true
  // Snapshot what's saved now so a regenerate is always undoable.
  const previous = hasContent.value ? {
    episodeTitle: props.project.episodeTitle || '',
    episodeThumbnailText: props.project.episodeThumbnailText || '',
    episodeDescription: props.project.episodeDescription || '',
  } : null
  try {
    const prompt = await episodeContentPromptService.resolvePrompt(props.project.showId)

    // The transcript is no longer sent from here: the function reads it from
    // episodeTranscripts/{projectId} so it gets the word-level timings too, which
    // is what keeps the generated timestamps inside the real episode.
    // showId lets the function append the show's title format + AI rules.
    const out = await openAIService.generateEpisodeContent(props.project.id, prompt, {
      showId: props.project.showId,
      showName: props.project.showName,
      episodeNumber: props.project.episodeNumber,
      episodeName: props.project.name,
    })
    title.value = out.title || title.value
    thumbnailText.value = out.thumbnailText || thumbnailText.value
    showNotes.value = out.showNotes || showNotes.value

    // Persist all three so the generated content isn't lost on navigation.
    await episodeService.updateEpisode(props.project.id, {
      episodeTitle: title.value,
      episodeThumbnailText: thumbnailText.value,
      episodeDescription: showNotes.value,
      episodeContentPrevious: previous,
      episodeContentStale: false,
    }, { note: 'AI generated' })
    toast.success('Title & show notes generated.')
  } catch (err) {
    logger.error('[EpisodeMetaEditor] Generate failed', { error: err.message })
    toast.error(err.message || 'Failed to generate')
  } finally {
    generating.value = false
  }
}
</script>

<style lang="scss" scoped>
.episode-meta-editor {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 1rem 1.25rem;
  gap: 0.5rem;
  overflow-y: auto;
}

.meta-locked {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  text-align: center;
}

.meta-toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding-bottom: 0.5rem;
  border-bottom: 1px solid hsl(0, 0%, 92%);
  margin-bottom: 0.5rem;
}

.meta-stale {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border: 1px solid hsl(40, 90%, 80%);
  border-radius: 6px;
  background: hsl(45, 100%, 97%);
}
.meta-stale-text {
  flex: 1;
  font-size: 0.85rem;
}

.meta-notes-field {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 240px;
}
.meta-notes-actions {
  text-align: right;
  margin-top: 0.25rem;
}
</style>
